/**
 * Search Utility Functions
 * 
 * Utilities for implementing multi-term search functionality and search result highlighting.
 */

import React from 'react';

import { globalSearchConfig } from './global-search-config.js';

/**
 * Split search query into individual terms and check if all terms match in the record
 * 
 * @param searchQuery - The search string to split and match
 * @param record - The record object to search in
 * @param searchableFields - Array of field names to search in
 * @param caseSensitive - Whether search should be case sensitive
 * @returns true if all search terms are found somewhere in the searchable fields
 */
export function matchesAllTerms(
  searchQuery: string,
  record: Record<string, any>,
  searchableFields: string[],
  caseSensitive: boolean = false
): boolean {
  if (!searchQuery.trim()) {return true;}
  
  // Split search query by spaces and filter out empty strings
  const searchTerms = searchQuery.trim().split(/\s+/).filter(term => term.length > 0);
  
  // Get all searchable field values as strings
  const searchableText = searchableFields
    .map(field => {
      const value = record[field];
      return value != null ? String(value) : '';
    })
    .join(' ');
  
  const textToSearch = caseSensitive ? searchableText : searchableText.toLowerCase();
  
  // Check if ALL terms are found somewhere in the searchable text
  const result = searchTerms.every(term => {
    const termToMatch = caseSensitive ? term : term.toLowerCase();
    return textToSearch.includes(termToMatch);
  });
  
  return result;
}

/** A full app PO / pick-ticket number, e.g. PO-20261013-T. Searched as typed. */
const PO_NUMBER_WITH_SUFFIX = /^(PO|PT)-\d+-[ST]$/i;

/**
 * PO searches (PO Tracker, /purchase-orders): PO numbers end in -S (slab) /
 * -T (tile), so a term ending in -s / -t is read as that department plus the
 * word before it — 'sunflower-s' = slab POs matching 'sunflower', and a bare
 * '-t' = tile POs only. A full PO number like 'PO-20261013-T' stays a plain
 * term. Returns the remaining text terms and the departments asked for (two
 * different departments match nothing).
 */
export function splitDepartmentSuffixTerms(
  terms: readonly string[]
): { terms: string[]; departments: Array<'slab' | 'tile'> } {
  const textTerms: string[] = [];
  const departments = new Set<'slab' | 'tile'>();
  for (const term of terms) {
    const suffix = /-([st])$/i.exec(term);
    if (!suffix || PO_NUMBER_WITH_SUFFIX.test(term)) {
      textTerms.push(term);
      continue;
    }
    departments.add(suffix[1].toLowerCase() === 's' ? 'slab' : 'tile');
    const word = term.slice(0, -2);
    if (word) {textTerms.push(word);}
  }
  return { terms: textTerms, departments: Array.from(departments) };
}

/** What a PO search highlights: the words, plus the departments' -S / -T. */
export interface PoSearchHighlight {
  text: string;
  departments: Array<'slab' | 'tile'>;
}

/**
 * Highlighting for a PO search (PO Tracker, /purchase-orders): the words with
 * the department suffix taken off ('sunflower-s' highlights 'sunflower'; a
 * bare '-t' highlights no word, so '-t' is never marked mid-text) and the
 * departments, whose -S / -T is marked at the end of PO numbers only.
 */
export function poSearchHighlightTerms(searchQuery: string | null | undefined): PoSearchHighlight {
  const { terms, departments } = splitDepartmentSuffixTerms(
    (searchQuery ?? '').trim().split(/\s+/).filter(Boolean)
  );
  return { text: terms.join(' '), departments };
}

/**
 * Adds a second search box's words (the tracker's line-item search) to a PO
 * search highlight, so a cell the PO search matches still highlights the
 * line-item words too. Departments stay the PO search's own.
 */
export function mergePoSearchHighlight(
  po: PoSearchHighlight,
  extra: string | null | undefined
): PoSearchHighlight {
  const words = [po.text, ...(extra ?? '').trim().split(/\s+/)].filter(Boolean);
  return { text: words.join(' '), departments: po.departments };
}

/**
 * Splits a PO number's trailing -S / -T off when the search asked for that
 * department, so it can be highlighted on its own (suffix is '' otherwise).
 * POs from before the suffix existed have none, so only words highlight. A
 * full number typed as a word ('PO-20261013-T') keeps the number whole so it
 * highlights as typed.
 */
export function splitPoNumberDepartmentSuffix(
  poNumber: string,
  highlight: PoSearchHighlight
): { base: string; suffix: string } {
  const whole = { base: poNumber, suffix: '' };
  if (!PO_NUMBER_WITH_SUFFIX.test(poNumber)) {return whole;}
  const department = poNumber.slice(-1).toLowerCase() === 's' ? 'slab' : 'tile';
  if (!highlight.departments.includes(department)) {return whole;}
  const lower = poNumber.toLowerCase();
  if (highlight.text.split(' ').some(t => /-[st]$/i.test(t) && lower.includes(t.toLowerCase()))) {
    return whole;
  }
  return { base: poNumber.slice(0, -2), suffix: poNumber.slice(-2) };
}

/**
 * Highlight search terms in text by wrapping them with a span element
 * 
 * @param text - The text to highlight terms in
 * @param searchQuery - The search query containing terms to highlight
 * @param options - Optional configuration for highlighting
 * @returns React element array with highlighted terms
 */
export function highlightSearchTerms(
  text: string | null | undefined,
  searchQuery: string | null | undefined,
  options?: {
    className?: string;
    style?: React.CSSProperties;
    caseSensitive?: boolean;
  }
): React.ReactElement[] | string {
  // Return original text if highlighting is disabled or no search query
  if (!globalSearchConfig.enableHighlighting || !searchQuery || !text) {
    return text || '';
  }

  const searchTerms = searchQuery.trim().split(/\s+/).filter(term => term.length > 0);
  if (searchTerms.length === 0) {
    return text;
  }

  // Use provided options or fall back to global config
  const className = options?.className || globalSearchConfig.highlightStyle?.className || '';
  const style = options?.style || globalSearchConfig.highlightStyle?.style;
  const caseSensitive = options?.caseSensitive ?? globalSearchConfig.caseSensitive;

  // Create regex pattern for all search terms
  const flags = caseSensitive ? 'g' : 'gi';
  const pattern = searchTerms.map(term => escapeRegExp(term)).join('|');
  const regex = new RegExp(`(${pattern})`, flags);

  // Split text by matches and create elements
  const parts = text.split(regex);
  
  return parts.map((part, index) => {
    // Check if this part matches any search term
    const isMatch = searchTerms.some(term => {
      const termToMatch = caseSensitive ? term : term.toLowerCase();
      const partToMatch = caseSensitive ? part : part.toLowerCase();
      return termToMatch === partToMatch;
    });

    if (isMatch) {
      return React.createElement(
        'span',
        {
          key: index,
          className,
          style,
        },
        part
      );
    }
    return part;
  }) as React.ReactElement[];
}

/**
 * Highlight search terms in text and return as HTML string
 * Useful for non-React contexts or when dangerouslySetInnerHTML is needed
 * 
 * @param text - The text to highlight terms in
 * @param searchQuery - The search query containing terms to highlight
 * @param options - Optional configuration for highlighting
 * @returns HTML string with highlighted terms
 */
export function highlightSearchTermsHTML(
  text: string | null | undefined,
  searchQuery: string | null | undefined,
  options?: {
    className?: string;
    style?: React.CSSProperties;
    caseSensitive?: boolean;
  }
): string {
  // Return original text if highlighting is disabled or no search query
  if (!globalSearchConfig.enableHighlighting || !searchQuery || !text) {
    return text || '';
  }

  const searchTerms = searchQuery.trim().split(/\s+/).filter(term => term.length > 0);
  if (searchTerms.length === 0) {
    return text;
  }

  const className = options?.className || globalSearchConfig.highlightStyle?.className || '';
  const style = options?.style || globalSearchConfig.highlightStyle?.style;
  const caseSensitive = options?.caseSensitive ?? globalSearchConfig.caseSensitive;

  // Convert style object to CSS string
  const styleString = style
    ? Object.entries(style)
        .map(([key, value]) => {
          // Convert camelCase to kebab-case
          const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
          return `${cssKey}: ${value}`;
        })
        .join('; ')
    : '';

  // Create regex pattern for all search terms
  const flags = caseSensitive ? 'g' : 'gi';
  const pattern = searchTerms.map(term => escapeRegExp(term)).join('|');
  const regex = new RegExp(`(${pattern})`, flags);

  // Replace matches with highlighted spans
  return text.replace(regex, (match) => {
    const classAttr = className ? `class="${className}"` : '';
    const styleAttr = styleString ? `style="${styleString}"` : '';
    const attrs = [classAttr, styleAttr].filter(Boolean).join(' ');
    return `<span ${attrs}>${match}</span>`;
  });
}

/**
 * Escape special regex characters in a string
 * 
 * @param string - The string to escape
 * @returns Escaped string safe for use in regex
 */
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}